import { Button } from "src/components/ui";
import React, { FC, Suspense, useEffect, useRef, useState } from 'react';
import { ErrorBoundary } from '@sentry/react';
import InlineLoading from 'src/components/common/InlineLoading/InlineLoading';
import { useHelpDrawer } from '../../../contexts/HelpDrawerContext';
import { FontAwesomeIcon, FontAwesomeIcons } from '../../../constants/fontAwesomeIcons';

import './HelpDrawer.scss';

const MIN_WIDTH = 500;
const STORAGE_KEY = 'helpDrawerWidth';
const HelpDrawerContent = React.lazy(() => import('./HelpDrawerContent'));

const HelpDrawer: FC = () => {
    const { isOpen, setIsOpen } = useHelpDrawer();
    const [hasOpened, setHasOpened] = useState(isOpen);

    useEffect(() => {
        if (isOpen) setHasOpened(true);
    }, [isOpen]);

    const getMaxWidth = () => window.innerWidth * 0.4;

    const [hasWidthChanged, setHasWidthChanged] = useState<boolean>(false);
    const [width, setWidth] = useState<number>(() => {
        const savedWidth = localStorage.getItem(STORAGE_KEY);
        return savedWidth ? parseInt(savedWidth, 10) : 500;
    });

    const isResizingRef = useRef<boolean>(false);
    const startXRef = useRef<number>(0);
    const startWidthRef = useRef<number>(0);
    const maxWidthRef = useRef<number>(getMaxWidth());

    useEffect(() => {
        const handleResize = () => {
            maxWidthRef.current = getMaxWidth();

            setWidth(prev => Math.min(prev, maxWidthRef.current));
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const rippleEffect = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        const ripple = document.createElement("span");
        ripple.classList.add("ripple");
        event.currentTarget.appendChild(ripple);
        setTimeout(() => ripple.remove(), 300);
    };

    const handleOnClickEvent: React.MouseEventHandler<HTMLButtonElement> = (event) => {
        rippleEffect(event);
        setIsOpen(!isOpen);
    };

    const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
        isResizingRef.current = true;
        startXRef.current = e.clientX;
        startWidthRef.current = width;

        document.body.style.userSelect = 'none';
        document.body.style.cursor = 'ew-resize';

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    };

    const handleMouseMove = (e: MouseEvent) => {
        if (!isResizingRef.current) return;
        const dx = startXRef.current - e.clientX;
        const newWidth = Math.min(
            Math.max(startWidthRef.current + dx, MIN_WIDTH),
            maxWidthRef.current
        );
        setWidth(newWidth);
    };

    const handleMouseUp = () => {
        if (!isResizingRef.current) return;
        isResizingRef.current = false;

        document.body.style.userSelect = '';
        document.body.style.cursor = '';

        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);

        setHasWidthChanged(true);
    };

    useEffect(() => {
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, []);

    useEffect(() => {
        if (hasWidthChanged) {
            localStorage.setItem(STORAGE_KEY, String(width));
            setHasWidthChanged(false);
        }
    }, [width, hasWidthChanged]);

    return (
        <div
            className="help-drawer-container"
            style={{
                width: isOpen ? `${width}px` : '0px',
            }}
        >
            <div
                className="help-drawer-fixed-container"
                style={{
                    width: isOpen ? `${width}px` : '0px',
                }}
            >
                <div style={{ height: '65px' }}></div>

                <div className='container'>
                    <div className='row pt-2'>
                        <div className='col-auto me-auto'>
                            <h1>Help</h1>
                        </div>
                        <div className='col-auto'>
                            <button
                                className="close-button close-button-transition"
                                onClick={handleOnClickEvent}
                                type="button"
                                aria-label="Close help"
                            >
                                <FontAwesomeIcon
                                    className="close-button-icon"
                                    icon={FontAwesomeIcons.Regular.TIMES}
                                    fixedWidth
                                />
                            </button>
                        </div>
                    </div>
                </div>

                <div className='quill-document-container custom-scrollbar'>
                    {(isOpen || hasOpened) && (
                        <ErrorBoundary fallback={
                            <div role="alert">
                                <p>Unable to load Help. Reload the page to try again.</p>
                                <Button type="button" variant="secondary" onClick={() => window.location.reload()}>
                                    Reload page
                                </Button>
                            </div>
                        }>
                            <Suspense fallback={<div role="status" aria-label="Loading help"><InlineLoading /></div>}>
                                <HelpDrawerContent />
                            </Suspense>
                        </ErrorBoundary>
                    )}
                </div>

                {isOpen && <div className="resize-handle" onMouseDown={handleMouseDown} />}
            </div>
        </div>
    );
};

export default HelpDrawer;
